import { useEffect } from "react";
import NextLink from "next/link";
import { useRouter } from "next/router";
import type { FlexProps, LinkProps } from "@chakra-ui/react";
import {
  Center,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import type { IconType } from "react-icons/lib";
import { RiArrowDownSLine, RiPlayListAddLine } from "react-icons/ri";
import { BsPersonPlusFill } from "react-icons/bs";
import usePermission from "hooks/use-permission";
import colorSchemes from "data/color-schemes";
import ActiveLink from "../app-nav/active-link";

interface ListLinkProps extends LinkProps {
  href: string;
  children: string;
  icon: IconType;
  iconColor?: string;
  cy?: string;
  isDisabled?: boolean;
}

const ListLink = ({
  href,
  children,
  icon,
  iconColor,
  cy,
  isDisabled,
}: ListLinkProps) => {
  const disabledProps: FlexProps = {
    opacity: 0.5,
    cursor: "not-allowed",
    tabIndex: -1,
  };

  const enabledProps: FlexProps = {
    _hover: {
      bg: "gray.100",
    },
    _active: {
      bg: "gray.200",
    },
    _focus: {
      outline: "none",
      shadow: "outline",
    },
  };

  return (
    <Flex
      as={isDisabled ? "div" : NextLink}
      href={href}
      align="center"
      p={2}
      rounded="md"
      fontWeight="medium"
      data-cy={`app-header-link-${cy}`}
      outline="none"
      transition="0.2s all"
      {...(isDisabled ? disabledProps : enabledProps)}
    >
      <Center
        mr={3}
        h={6}
        w={6}
        rounded="md"
        bg={`${iconColor}.100`}
        borderWidth={1}
        borderColor={`${iconColor}.200`}
      >
        <Icon as={icon} color={`${iconColor}.500`} />
      </Center>
      {children}
    </Flex>
  );
};

const ListDropdown = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const router = useRouter();

  useEffect(() => {
    router.events.on("routeChangeStart", onClose);

    return () => {
      router.events.off("routeChangeStart", onClose);
    };
  }, [router.events, onClose]);

  const [
    hasWatchlistPermission,
    hasContactListPermission,
  ] = usePermission([
    "advancedWatchlist",
    "contactList",
  ]);

  return (
    <Popover trigger="hover" isOpen={isOpen}>
      <PopoverTrigger>
        <Flex
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          onFocus={onOpen}
          onBlur={onClose}
          align="center"
          position="relative"
          {...(isOpen && {
            _after: {
              content: "''",
              position: "absolute",
              w: "150%",
              h: "8px",
              bottom: "-8px",
              left: "-33.33%",
            },
          })}
          _focus={{ outline: "none", shadow: "outline" }}
        >
          <ActiveLink href="/watchlists">
            <Flex align="center">
              Watchlists <Icon ml={2} as={RiArrowDownSLine} />
            </Flex>
          </ActiveLink>
        </Flex>
      </PopoverTrigger>

      <PopoverContent
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        onFocus={onOpen}
        onBlur={onClose}
        w="auto"
        shadow="xl"
        rounded="xl"
      >
        <PopoverBody p={2}>
          <Stack alignItems="stretch" spacing={1}>
            {hasWatchlistPermission && (
              <ListLink
                href="/watchlists"
                icon={RiPlayListAddLine}
                iconColor={colorSchemes.company}
                cy="watchlist"
              >
                Company Watchlists
              </ListLink>
            )}
            {hasContactListPermission && (
              <ListLink
                href="/contact-lists"
                icon={BsPersonPlusFill}
                iconColor={colorSchemes.people}
                cy="contact-list"
              >
                Contact Download Lists
              </ListLink>
            )}
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default ListDropdown;
